// src/pages/Admin/CoursAgenda/components/CoursAgendaFilters.jsx

import React from "react";

import {
  FaFilter,
  FaSearch,
  FaTimes,
  FaUser,
  FaUserFriends,
  FaMapMarkerAlt,
} from "react-icons/fa";

import { getPersonName } from "../utils/coursAgendaUtils";

const CONTROL_CLASS = `
  cours-agenda-control
  w-full
  rounded-xl
  border border-slate-200
  bg-white
  px-3.5 py-2.5
  text-sm
  text-slate-900
  outline-none
  transition
  placeholder:text-slate-400
  focus:border-red-500
  focus:ring-4
  focus:ring-red-500/10
  dark:border-slate-700
  dark:bg-slate-950
  dark:text-slate-100
  dark:placeholder:text-slate-500
`;

const CoursAgendaFilters = ({
  filters,
  setFilters,

  lieuxOptions = [],
  enseignants = [],
  assistants = [],

  resetFilters,
  activeFiltersCount = 0,
}) => {
  const setFilter = (name, value) => {
    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  return (
    <section
      className="
        rounded-2xl
        border border-slate-200
        bg-white
        p-4
        shadow-sm
        dark:border-slate-800
        dark:bg-slate-900/70
        sm:p-5
      "
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FaFilter className="text-red-500" />

            <h3 className="font-semibold text-slate-900 dark:text-white">
              Rechercher et filtrer
            </h3>

            {activeFiltersCount > 0 && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/50 dark:text-red-300">
                {activeFiltersCount}
              </span>
            )}
          </div>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Les filtres s'appliquent immédiatement au planning.
          </p>
        </div>

        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={resetFilters}
            className="
              flex items-center gap-2 rounded-xl
              px-3 py-2 text-sm font-medium
              text-slate-600
              transition
              hover:bg-slate-100 hover:text-slate-950
              dark:text-slate-300
              dark:hover:bg-slate-800 dark:hover:text-white
            "
          >
            <FaTimes />

            <span className="hidden sm:inline">
              Réinitialiser
            </span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {/* Recherche */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Recherche
          </label>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="search"
              value={filters.search}
              onChange={(event) =>
                setFilter(
                  "search",
                  event.target.value
                )
              }
              placeholder="Cours, thème, lieu..."
              className={`${CONTROL_CLASS} pl-10`}
            />
          </div>
        </div>

        {/* Lieu */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Lieu
          </label>

          <div className="relative">
            <FaMapMarkerAlt className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400" />

            <select
              value={filters.lieu}
              onChange={(event) =>
                setFilter(
                  "lieu",
                  event.target.value
                )
              }
              className={`${CONTROL_CLASS} pl-10`}
            >
              <option value="">
                Tous les lieux
              </option>

              {lieuxOptions.map((lieu) => (
                <option key={lieu} value={lieu}>
                  {lieu}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Instructeur */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Instructeur
          </label>

          <div className="relative">
            <FaUser className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400" />

            <select
              value={filters.enseignantId}
              onChange={(event) =>
                setFilter(
                  "enseignantId",
                  event.target.value
                )
              }
              className={`${CONTROL_CLASS} pl-10`}
            >
              <option value="">
                Tous les instructeurs
              </option>

              <option value="none">
                Sans instructeur
              </option>

              {enseignants.map(
                (enseignant) => (
                  <option
                    key={enseignant.id}
                    value={enseignant.id}
                  >
                    {getPersonName(
                      enseignant
                    )}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {/* Assistant */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Assistant
          </label>

          <div className="relative">
            <FaUserFriends className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-400" />

            <select
              value={filters.assistantId}
              onChange={(event) =>
                setFilter(
                  "assistantId",
                  event.target.value
                )
              }
              className={`${CONTROL_CLASS} pl-10`}
            >
              <option value="">
                Tous les assistants
              </option>

              <option value="none">
                Sans assistant
              </option>

              {assistants.map(
                (assistant) => (
                  <option
                    key={assistant.id}
                    value={assistant.id}
                  >
                    {getPersonName(
                      assistant
                    )}
                  </option>
                )
              )}
            </select>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoursAgendaFilters;